import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { 
  IoFastFoodOutline, 
  IoWalkOutline, 
  IoHappyOutline, 
  IoScaleOutline, 
  IoMoonOutline, 
  IoWaterOutline, 
  IoRadioOutline,
  IoTimeOutline,
  IoReaderOutline,
  IoBodyOutline,
  IoFlameOutline,
  IoPulseOutline,
  IoTrashOutline,
  IoRestaurantOutline
} from "react-icons/io5";
import { FiEdit, FiTrash2 } from "react-icons/fi";

import {
  GetLifestyleEntries,
  AddLifestyleEntry,
  GetDietRecommendations,
  DeleteLifestyleEntry,
  GetLifestyleConfig
} from "../../../StateManagement/Reducers/LifestyleState";

import {
  Container,
  HeaderSection,
  Title,
  AddButton,
  LayoutGrid,
  LeftColumn,
  RightColumn,
  Card,
  JournalList,
  JournalItem,
  IconWrapper,
  JournalText,
  JournalTitle,
  JournalSubtitle,
  EmptyState,
  ActionLink,
  EntryCard,
  EntryCardHeader,
  Badge,
  ActionIcons,
  EntryBody,
  DataItem,
  DataLabel,
  NestedSection,
  SectionHeader,
  NestedItem,
  NestedText,
  NestedSubtext,
  RecommendationTitle
} from "./styles";

import LifestyleCalendar from "./LifestyleCalendar";
import AddEntryModal from "./AddEntryModal";

const journalCategories = [
  { id: "food", label: "Food", icon: <IoRestaurantOutline />, color: "#407BFF", bgColor: "#EBF2FF" },
  { id: "activity", label: "Activity", icon: <IoPulseOutline />, color: "#407BFF", bgColor: "#EBF2FF" },
  { id: "mood", label: "Mood", icon: <IoHappyOutline />, color: "#407BFF", bgColor: "#EBF2FF" },
  { id: "measurement", label: "Measurement", icon: <IoScaleOutline />, color: "#407BFF", bgColor: "#EBF2FF" },
  { id: "sleep", label: "Sleep", icon: <IoMoonOutline />, color: "#407BFF", bgColor: "#EBF2FF" },
  { id: "water", label: "Water", icon: <IoWaterOutline />, color: "#407BFF", bgColor: "#EBF2FF" },
  { id: "bowel movement", label: "Bowel Movement", icon: <IoRadioOutline />, color: "#407BFF", bgColor: "#EBF2FF" },
];

const LifestyleDiet = () => {
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  const { entries, recommendations, config } = useSelector((state) => state.Lifestyle);

  const visibleCategories = React.useMemo(() => {
    return journalCategories.filter((cat) => {
      if (config && Object.keys(config).length > 0) {
        return config[cat.label] !== false;
      }
      return true;
    });
  }, [config]);

  useEffect(() => {
    dispatch(GetLifestyleEntries());
    dispatch(GetDietRecommendations());
    dispatch(GetLifestyleConfig());
  }, [dispatch, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleSaveEntry = (data) => {
    dispatch(AddLifestyleEntry({ 
      ...data, 
      date: moment(selectedDate).format("YYYY-MM-DD") 
    }));
    setShowModal(false);
    setEditingEntry(null);
  };

  const handleDelete = (id, pid) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
        dispatch(DeleteLifestyleEntry({ id, pid }));
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setShowModal(true);
  };

  const currentDayEntries = entries.filter(e => {
    if (e.date !== moment(selectedDate).format("YYYY-MM-DD")) return false;
    // Only display entries for categories that are currently enabled in the config
    return visibleCategories.some(vc => vc.id === e.type);
  });

  const renderEntryData = (entry) => {
    // Robust data extraction: handle nested format, flat format, and merged keys
    const rawData = entry.data || {};
    const nestedData = rawData.data || {};
    
    // Merge everything for easy lookup with fallbacks
    const data = { ...rawData, ...nestedData };
    
    // Normalize entry type for matching
    const entryType = (entry.type || entry.category || "").toLowerCase();

    const items = [
      { icon: <IoTimeOutline />, label: "Time", value: moment(entry.timestamp).format("hh:mm A") },
    ];

    switch (entryType) {
      case "food":
        items.push(
          { icon: <IoRestaurantOutline />, label: "Meal Type", value: data.meal_type || data.mealType },
          { icon: <IoReaderOutline />, label: "Description", value: data.description, span: 2 }
        );
        break;
      case "activity":
        items.push(
          { icon: <IoPulseOutline />, label: "Description", value: data.description },
          { icon: <IoTimeOutline />, label: "Duration", value: data.duration },
          { icon: <IoFlameOutline />, label: "Calories Burned", value: data.calories || data.calories_burned }
        );
        break;
      case "mood":
        items.push(
          { icon: <IoHappyOutline />, label: "Mood", value: data.mood },
          { icon: <IoReaderOutline />, label: "Summary", value: data.summary, span: 2 }
        );
        break;
      case "measurement":
        items.push(
          { icon: <IoBodyOutline />, label: "Height", value: data.height },
          { icon: <IoScaleOutline />, label: "Weight", value: data.weight }
        );
        break;
      case "sleep":
        items.push(
          { icon: <IoTimeOutline />, label: "Hours", value: data.hours || data.duration },
          { icon: <IoReaderOutline />, label: "Quality", value: data.quality }
        );
        break;
      case "water":
        items.push(
          { icon: <IoWaterOutline />, label: "Amount", value: data.amount }
        );
        break;
      case "bowel":
      case "bowel movement":
        items.push(
          { icon: <IoRadioOutline />, label: "Type", value: data.type || data.frequency },
          { icon: <IoReaderOutline />, label: "Notes", value: data.notes || data.color, span: 2 }
        );
        break;
      default:
        break;
    }

    return items.map((item, i) => (
      <DataItem key={i} style={item.span ? { gridColumn: `span ${item.span}` } : {}}>
        <IconWrapper color="#ffffff" bgColor="#4178ff" style={{ width: "32px", height: "32px", fontSize: "16px" }}>
          {item.icon}
        </IconWrapper>
        <DataLabel>
          <span className="label">{item.label}</span>
          <span className="value">{item.value || "-"}</span>
        </DataLabel>
      </DataItem>
    ));
  };

  return (
    <Container>
      <HeaderSection>
        <Title>Lifestyle & Diet Recommendation</Title>
        <AddButton onClick={() => setShowModal(true)}>Add Lifestyle</AddButton>
      </HeaderSection>

      <LayoutGrid>
        <LeftColumn>
          <LifestyleCalendar 
            selectedDate={selectedDate} 
            onDateChange={handleDateChange} 
            entries={entries}
          />
          
          <Card>
            <div style={{ fontSize: "15px", fontWeight: "600", marginBottom: "16px" }}>
              My Journal - {moment(selectedDate).format("MMMM D")}
            </div>
            <JournalList>
              {visibleCategories.map((cat) => {
                const count = currentDayEntries.filter(e => e.type === cat.id).length;
                return (
                  <JournalItem key={cat.id}>
                    <IconWrapper color={cat.color} bgColor={cat.bgColor}>
                      {cat.icon}
                    </IconWrapper>
                    <JournalText>
                      <JournalTitle>{cat.label}</JournalTitle>
                      <JournalSubtitle>{count} entries</JournalSubtitle>
                    </JournalText>
                  </JournalItem>
                );
              })}
            </JournalList>
          </Card>
        </LeftColumn>

        <RightColumn>
          <div style={{ marginBottom: "24px" }}>
            <div style={{ fontSize: "16px", fontWeight: "600", color: "#111827", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                <span>Lifestyle Entries</span>
                <span style={{ background: "#f3f4f6", color: "#6b7280", fontSize: "12px", padding: "2px 8px", borderRadius: "12px" }}>{currentDayEntries.length}</span>
            </div>
            
            {currentDayEntries.length === 0 ? (
                <EmptyState>
                <img src="https://cdni.iconscout.com/illustration/premium/thumb/empty-state-2130362-1800926.png" alt="No entries" style={{ width: "200px", opacity: 0.8 }} />
                <h3>No entries for this date</h3>
                <p style={{ fontSize: "14px", color: "#6b7280", margin: "8px 0" }}>Update your health metrics now</p>
                <ActionLink onClick={() => setShowModal(true)}>Add your first entry</ActionLink>
                </EmptyState>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {currentDayEntries.map((entry, index) => (
                        <EntryCard key={index}>
                            <EntryCardHeader>
                                <h4>{journalCategories.find(c => c.id === entry.type)?.label}</h4>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Badge status={entry.status}>{entry.status === "Reviewed" ? "Reviewed" : "Review Pending"}</Badge>
                                    <ActionIcons disabled={entry.status === "Reviewed"}>
                                        <FiEdit className="edit" onClick={(e) => { e.stopPropagation(); handleEdit(entry); }} />
                                        <FiTrash2 className="delete" onClick={(e) => { e.stopPropagation(); handleDelete(entry.id, entry.pid); }} />
                                    </ActionIcons>
                                </div>
                            </EntryCardHeader>
                            <EntryBody>
                                {renderEntryData(entry)}
                            </EntryBody>

                            {/* Nested Sections for Comments and Recommendations */}
                            {entry.comments && entry.comments.length > 0 && (
                                <NestedSection>
                                    <SectionHeader>Comment</SectionHeader>
                                    {entry.comments.map((comment, i) => (
                                        <NestedItem key={i}>
                                            <NestedText>{comment.text}</NestedText>
                                            <NestedSubtext>{comment.author || entry.providerName || "Provider"} {comment.date || moment(entry.timestamp).format("YYYY-MM-DD, hh:mm A")}</NestedSubtext>
                                        </NestedItem>
                                    ))}
                                </NestedSection>
                            )}

                            {entry.diet_recommendations && entry.diet_recommendations.length > 0 && (
                                <NestedSection>
                                    <SectionHeader>Diet Recommendation</SectionHeader>
                                    {entry.diet_recommendations.map((rec, i) => (
                                        <NestedItem key={i}>
                                            <RecommendationTitle>{rec.title}</RecommendationTitle>
                                            <NestedText style={{ fontWeight: 400, color: '#4b5563' }}>{rec.description}</NestedText>
                                            <NestedSubtext>{rec.author || entry.providerName || "Provider"} {rec.date || moment(entry.timestamp).format("YYYY-MM-DD, hh:mm A")}</NestedSubtext>
                                        </NestedItem>
                                    ))}
                                </NestedSection>
                            )}
                        </EntryCard>
                    ))}
                </div>
            )}
          </div>

          <div style={{ marginTop: "32px" }}>
             <Card style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: "16px 20px", borderBottom: '1px solid #f3f4f6' }}>
                   <Title style={{ fontSize: "16px" }}>Balanced Diet Recommendations</Title>
                </div>
                <div style={{ padding: "20px" }}>
                  {(() => {
                      const filteredRecs = recommendations.filter(rec => rec.entry_date === moment(selectedDate).format("YYYY-MM-DD"));
                      return filteredRecs.length > 0 ? (
                          filteredRecs.map((rec, i) => (
                              <DataItem key={i} style={{ marginBottom: i < filteredRecs.length - 1 ? "20px" : 0 }}>
                                  <IconWrapper color="#ffffff" bgColor="#4178ff" style={{ width: "36px", height: "36px", fontSize: "18px" }}>
                                      <IoRestaurantOutline />
                                  </IconWrapper>
                                  <DataLabel style={{ gap: "4px" }}>
                                      <span className="value" style={{ fontSize: "15px" }}>{rec.title}</span>
                                      <span className="label" style={{ fontSize: "13px", lineHeight: "1.4" }}>{rec.description}</span>
                                      <span className="label" style={{ marginTop: "6px", fontSize: "12px", color: "#9ca3af" }}>{rec.providerName} {rec.entry_date}</span>
                                  </DataLabel>
                              </DataItem>
                          ))
                      ) : (
                          <p style={{ color: "#6b7280", margin: 0, fontSize: "14px", textAlign: "center" }}>No diet recommendations assigned for this date.</p>
                      );
                  })()}
                </div>
             </Card>
          </div>
        </RightColumn>
      </LayoutGrid>

      {showModal && (
        <AddEntryModal 
          onClose={() => {
            setShowModal(false);
            setEditingEntry(null);
          }} 
          onSave={handleSaveEntry} 
          editingData={editingEntry}
        />
      )}
    </Container>
  );
};

export default LifestyleDiet;
